// ImageLightbox.jsx
import React, { useState, useRef } from 'react';
import { Box, CircularProgress, IconButton, Stack, Tooltip } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ActionModal from 'ui-component/Modals/ActionModal'; // Adjust path as needed

const ImageLightbox = ({ open, imageUrl, onClose, altText = "Screenshot" }) => {
    const [imageLoading, setImageLoading] = useState(true);
    const [imageError, setImageError] = useState(false);
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const imageRef = useRef(null);

    const handleClose = () => {
        setImageLoading(true);
        setImageError(false);
        setScale(1);
        setPosition({ x: 0, y: 0 });
        onClose();
    };

    const handleZoomIn = () => {
        setScale(prevScale => Math.min(prevScale + 0.25, 3));
    };

    const handleZoomOut = () => {
        setScale(prevScale => Math.max(prevScale - 0.25, 0.5));
    };

    const handleReset = () => {
        setScale(1);
        setPosition({ x: 0, y: 0 });
    };

    const handleMouseDown = (e) => {
        if (scale > 1) {
            setIsDragging(true);
            setDragStart({
                x: e.clientX - position.x,
                y: e.clientY - position.y
            });
        }
    };

    const handleMouseMove = (e) => {
        if (isDragging && scale > 1) {
            setPosition({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleWheel = (e) => {
        e.preventDefault();
        if (e.deltaY < 0) {
            handleZoomIn();
        } else {
            handleZoomOut();
        }
    };

    return (
        <ActionModal
            open={open}
            onClose={handleClose}
            title="Metadata"
            width="90vw"
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                }}
            >
                {/* Zoom Controls */}
                {!imageLoading && !imageError && (
                    <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="Zoom In">
                            <IconButton
                                onClick={handleZoomIn}
                                disabled={scale >= 3}
                                size="small"
                                sx={{
                                    bgcolor: 'background.paper',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    '&:hover': { bgcolor: 'action.hover' }
                                }}
                            >
                                <ZoomInIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Zoom Out">
                            <IconButton
                                onClick={handleZoomOut}
                                disabled={scale <= 0.5}
                                size="small"
                                sx={{
                                    bgcolor: 'background.paper',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    '&:hover': { bgcolor: 'action.hover' }
                                }}
                            >
                                <ZoomOutIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Reset">
                            <IconButton
                                onClick={handleReset}
                                size="small"
                                sx={{
                                    bgcolor: 'background.paper',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    '&:hover': { bgcolor: 'action.hover' }
                                }}
                            >
                                <RestartAltIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                px: 2,
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                fontSize: '14px',
                                fontWeight: 500
                            }}
                        >
                            {Math.round(scale * 100)}%
                        </Box>
                    </Stack>
                )}

                {/* Image Container */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '400px',
                        maxHeight: '70vh',
                        position: 'relative',
                        bgcolor: '#f5f5f5',
                        borderRadius: 1,
                        p: 2,
                        overflow: 'hidden',
                        cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
                    }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onWheel={handleWheel}
                >
                    {imageLoading && !imageError && (
                        <CircularProgress
                            sx={{
                                position: 'absolute'
                            }}
                        />
                    )}

                    {imageError ? (
                        <Box
                            sx={{
                                color: 'text.secondary',
                                textAlign: 'center',
                                p: 4,
                                fontSize: '16px'
                            }}
                        >
                            Failed to load image
                        </Box>
                    ) : (
                        <img
                            ref={imageRef}
                            src={imageUrl}
                            alt={altText}
                            onLoad={() => setImageLoading(false)}
                            onError={() => {
                                setImageLoading(false);
                                setImageError(true);
                            }}
                            style={{
                                maxWidth: '100%',
                                maxHeight: '70vh',
                                objectFit: 'contain',
                                display: imageLoading ? 'none' : 'block',
                                borderRadius: '4px',
                                transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                                transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                                userSelect: 'none',
                                pointerEvents: 'none'
                            }}
                            draggable={false}
                        />
                    )}
                </Box>
            </Box>
        </ActionModal>
    );
};

export default ImageLightbox;